import { cn } from '@kognitos/lattice';

/** Tone applied to the status dot. */
export type TStatusDotTone =
  | 'success'
  | 'muted'
  | 'destructive'
  | 'warning'
  | 'info'
  | 'primary'
  | 'hollow';

/** Dot diameter. */
export type TStatusDotSize = 'sm' | 'md';

/**
 * Props for the StatusDot component.
 */
export interface IStatusDotProps {
  /** Tone of the dot. Defaults to 'success'. */
  tone?: TStatusDotTone;
  /** Diameter: 'sm' → size-1.5 (6px), 'md' → size-2 (8px). Defaults to 'sm'. */
  size?: TStatusDotSize;
  /** When true, the dot pulses (animate-pulse). Defaults to false. */
  pulse?: boolean;
  /** Optional className. */
  className?: string;
}

/** Maps a tone to its background class. */
const TONE_CLASS: Record<TStatusDotTone, string> = {
  success: 'bg-success',
  muted: 'bg-muted-foreground',
  destructive: 'bg-destructive',
  warning: 'bg-warning',
  info: 'bg-info',
  primary: 'bg-primary',
  hollow: 'border border-ring bg-card',
};

/** Maps a size to its diameter class (uses `size-*` so width === height). */
const SIZE_CLASS: Record<TStatusDotSize, string> = {
  sm: 'size-1.5',
  md: 'size-2',
};

/**
 * A small circular status dot, built on bumblebee's established status-dot
 * pattern (`size-* shrink-0 rounded-full`, as used on automation cards).
 *
 * The `shrink-0` is load-bearing: a fixed-size dot placed inside a flex row
 * gets compressed into an ellipse when horizontal space is tight (flex children
 * shrink by default). `size-*` guarantees equal width/height so it stays a
 * perfect circle at every viewport width. Optionally pulses.
 *
 * @param {IStatusDotProps} props - Component props
 * @returns {JSX.Element} The rendered status dot
 *
 * @example
 * <StatusDot tone="success" pulse />
 */
export function StatusDot({
  tone = 'success',
  size = 'sm',
  pulse = false,
  className,
}: IStatusDotProps) {
  return (
    <span
      aria-hidden
      className={cn(
        'aspect-square shrink-0 rounded-full',
        SIZE_CLASS[size],
        TONE_CLASS[tone],
        pulse && 'animate-pulse',
        className,
      )}
    />
  );
}
